
/**
 * Toast — auto-dismissing notification.
 *
 * Usage:
 *   const { addToast } = useToast();
 *   addToast({ type: 'success', message: 'Saved' });
 *
 * Wrap the app with <ToastProvider> once (already in App.tsx).
 */

import React, { createContext, useContext, useCallback, useState, useRef, useEffect } from 'react';
import './Toast.css';

/* ---------- types ---------- */
type ToastType = 'info' | 'success' | 'warning' | 'error';

interface ToastItem {
  id: number;
  type: ToastType;
  message: string;
  duration: number; /* ms */
}

interface ToastAPI {
  addToast: (t: Omit<ToastItem, 'id' | 'duration'> & { duration?: number }) => void;
}

/* ---------- context ---------- */
const ToastCtx = createContext<ToastAPI>({ addToast: () => {} });
export const useToast = () => useContext(ToastCtx);

/* ---------- single toast ---------- */
function Toast({ item, onDismiss }: { item: ToastItem; onDismiss: (id: number) => void }) {
  const [exiting, setExiting] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    timer.current = setTimeout(() => setExiting(true), item.duration);
    return () => clearTimeout(timer.current!);
  }, [item.duration]);

  const handleEnd = () => {
    if (exiting) onDismiss(item.id);
  };

  const icon: Record<ToastType, string> = {
    info: 'ℹ',
    success: '✓',
    warning: '⚠',
    error: '✕',
  };

  return (
    <div
      className={`boc-toast boc-toast--${item.type} ${exiting ? 'boc-toast--exit' : ''}`}
      onAnimationEnd={handleEnd}
      role="alert"
    >
      <span className="boc-toast__icon">{icon[item.type]}</span>
      <span className="boc-toast__msg">{item.message}</span>
      <button
        type="button"
        className="boc-toast__close"
        onClick={() => setExiting(true)}
        aria-label="Dismiss"
      >
        ✕
      </button>
    </div>
  );
}

/* ---------- provider ---------- */
let _nextId = 0;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const addToast: ToastAPI['addToast'] = useCallback((t) => {
    setToasts((prev) => [
      ...prev,
      { id: ++_nextId, type: t.type, message: t.message, duration: t.duration ?? 4000 },
    ]);
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastCtx.Provider value={{ addToast }}>
      {children}
      <div className="boc-toast-container" aria-live="polite">
        {toasts.map((t) => (
          <Toast key={t.id} item={t} onDismiss={dismiss} />
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
